import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGuestAuth } from '../contexts/GuestAuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Google OAuth Callback Handler
 * 
 * This component handles the redirect from Emergent Auth after Google sign-in.
 * It extracts the session_id from the URL fragment and exchanges it for user data.
 * 
 * CRITICAL: This must be rendered BEFORE ProtectedRoute checks to avoid race conditions.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processOAuthCallback } = useGuestAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment (hash)
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id in callback URL');
          navigate('/', { replace: true });
          return;
        }

        // Exchange session_id for user data
        const userData = await processOAuthCallback(sessionId);
        
        // Clear the hash from URL and redirect to home with user data
        window.history.replaceState(null, '', window.location.pathname);
        
        // Redirect to home page (or wherever the user should go after login)
        navigate('/', { 
          replace: true,
          state: { user: userData, justLoggedIn: true }
        });

      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/', { 
          replace: true,
          state: { authError: 'Failed to sign in. Please try again.' }
        });
      }
    };

    processAuth();
  }, [location.hash, processOAuthCallback, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900">
      <Loader2 className="h-12 w-12 animate-spin text-amber-400 mb-4" />
      <p className="text-white text-lg">Signing you in...</p>
    </div>
  );
};

export default AuthCallback;
